import type { CSSProperties, ReactNode } from 'react';

const DEFAULT_SELECTED_CLASSES = 'bg-white text-gray-900 font-semibold shadow-sm';
const UNSELECTED_CLASSES = 'text-gray-500 font-medium hover:text-gray-700';

export interface SegmentedOption<T extends string> {
    value: T;
    label: string;
    /** Replaces the default white selected tone, e.g. a team colour. */
    selectedClassName?: string;
    selectedStyle?: CSSProperties;
    /** Rendered before the label, e.g. a colour dot. */
    leading?: (selected: boolean) => ReactNode;
    /** Greys out just this option. */
    disabled?: boolean;
}

interface SegmentedToggleProps<T extends string> {
    value: T;
    options: readonly SegmentedOption<T>[];
    onChange: (value: T) => void;
    ariaLabel: string;
    disabled?: boolean;
}

export function SegmentedToggle<T extends string>({
    value,
    options,
    onChange,
    ariaLabel,
    disabled = false
}: SegmentedToggleProps<T>) {
    return (
        <div
            role="radiogroup"
            aria-label={ariaLabel}
            aria-disabled={disabled || undefined}
            className={`flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5 h-9 ${disabled ? 'opacity-50' : ''}`}
        >
            {options.map((option) => {
                const selected = option.value === value;
                const tone = selected
                    ? (option.selectedClassName ?? DEFAULT_SELECTED_CLASSES)
                    : UNSELECTED_CLASSES;
                const layout = option.leading ? 'flex items-center justify-center gap-1.5' : '';
                const dimmed = option.disabled && !disabled ? 'opacity-50' : '';
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={disabled || option.disabled}
                        onClick={() => onChange(option.value)}
                        style={selected ? option.selectedStyle : undefined}
                        className={`flex-1 h-full px-3 rounded-md text-xs whitespace-nowrap transition-colors duration-200 disabled:cursor-not-allowed ${layout} ${tone} ${dimmed}`}
                    >
                        {option.leading?.(selected)}
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
